import { cocCategories, cocImpactedElements } from '../../services/mockData';

const COCRequestDetail = ({ request, onClose }) => {
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Submitted':
        return 'bg-blue';
      case 'Under Review':
        return 'bg-yellow';
      case 'Approved':
        return 'bg-green';
      case 'Rejected':
        return 'bg-red';
      case 'More Info Required':
        return 'bg-orange';
      default:
        return 'bg-secondary';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const category = cocCategories.find(cat => cat.id === request.category);
  const impactedElementNames = request.impactedElements.map(id => {
    const element = cocImpactedElements.find(el => el.id === id);
    return element ? element.name : id;
  });

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">COC Request Details</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {/* Status Banner */}
            <div className={`alert alert-${request.status === 'Approved' ? 'success' : request.status === 'Rejected' ? 'danger' : 'info'} mb-4`}>
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <span className={`badge ${getStatusBadgeClass(request.status)} badge-lg`}>
                    {request.status}
                  </span>
                </div>
                <div>
                  <div className="fw-bold">Request ID: COC-{request.id}</div>
                  <div className="text-secondary small">
                    Submitted {formatDate(request.submittedDate)} by {request.submittedBy}
                  </div>
                </div>
              </div>
            </div>

            {/* Category Information */}
            <div className="card mb-3">
              <div className="card-header">
                <h4 className="card-title">COC Category</h4>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-2">
                      <span className="text-secondary">Category:</span>
                      <span className="fw-bold ms-2">{request.categoryName}</span>
                    </div>
                    {category && (
                      <>
                        <div className="mb-2">
                          <span className="text-secondary">Description:</span>
                          <span className="ms-2">{category.description}</span>
                        </div>
                        <div className="alert alert-azure mb-0">
                          <i className="ti ti-shield-check me-2"></i>
                          {category.complianceNote}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <span className="text-secondary">Discovery Date:</span>
                      <span className="fw-bold ms-2">
                        {new Date(request.discoveryDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-secondary">Loan Number:</span>
                      <span className="fw-bold ms-2">{request.loanNumber}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Change Description */}
            <div className="card mb-3">
              <div className="card-header">
                <h4 className="card-title">Change Description</h4>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">What Changed?</label>
                  <div className="text-secondary">{request.description}</div>
                </div>
                <div>
                  <label className="form-label fw-bold">Why Was This Not Known at Initial Disclosure?</label>
                  <div className="text-secondary">{request.reasonNotKnown}</div>
                </div>
              </div>
            </div>

            {/* Impacted Elements */}
            <div className="card mb-3">
              <div className="card-header">
                <h4 className="card-title">Impacted Loan Elements</h4>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  {impactedElementNames.map((name, idx) => (
                    <div key={idx} className="col-auto">
                      <span className="badge bg-primary-lt">
                        <i className="ti ti-check me-1"></i>
                        {name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Supporting Documents */}
            <div className="card mb-3">
              <div className="card-header">
                <h4 className="card-title">Supporting Documentation</h4>
              </div>
              <div className="card-body">
                <div className="list-group list-group-flush">
                  {request.supportingDocuments.map(doc => (
                    <div key={doc.id} className="list-group-item">
                      <div className="row align-items-center">
                        <div className="col-auto">
                          <i className="ti ti-file-text text-primary" style={{ fontSize: '2rem' }}></i>
                        </div>
                        <div className="col">
                          <div className="fw-bold">{doc.fileName}</div>
                          <div className="text-secondary small">
                            {doc.fileSize} • Uploaded {formatDate(doc.uploadDate)}
                          </div>
                          {doc.label && (
                            <div className="mt-1">
                              <span className="badge bg-azure-lt">{doc.label}</span>
                            </div>
                          )}
                        </div>
                        <div className="col-auto">
                          <button className="btn btn-sm btn-outline-primary">
                            <i className="ti ti-download me-1"></i>
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Attestation */}
            <div className="card mb-3">
              <div className="card-header">
                <h4 className="card-title">Broker Attestation</h4>
              </div>
              <div className="card-body">
                <div className="alert alert-danger mb-0">
                  <div className="d-flex">
                    <div>
                      <i className="ti ti-shield-check icon alert-icon"></i>
                    </div>
                    <div>
                      <div className="fw-bold mb-2">Compliance Attestation</div>
                      <div className="text-secondary">
                        {request.attestationText}
                      </div>
                      <div className="mt-3">
                        <i className="ti ti-check text-success me-2"></i>
                        <span className="fw-bold">Attested by {request.submittedBy} on {formatDate(request.submittedDate)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Review Information */}
            {request.reviewedBy && (
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">Lender Review</h4>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <div className="text-secondary small">Reviewed By</div>
                      <div className="fw-bold">{request.reviewedBy}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-secondary small">Review Date</div>
                      <div className="fw-bold">{formatDate(request.reviewedDate)}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-secondary small">Status</div>
                      <div>
                        <span className={`badge ${getStatusBadgeClass(request.status)}`}>
                          {request.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  {request.reviewNotes && (
                    <div>
                      <div className="text-secondary small mb-1">Review Notes</div>
                      <div className="alert alert-info mb-0">
                        {request.reviewNotes}
                      </div>
                    </div>
                  )}
                  {request.revisedDisclosureIssued && (
                    <div className="mt-3">
                      <div className="alert alert-success mb-0">
                        <div className="d-flex">
                          <div>
                            <i className="ti ti-file-check icon alert-icon"></i>
                          </div>
                          <div>
                            <div className="fw-bold">Revised Loan Estimate Issued</div>
                            <div className="text-secondary">
                              Issued on {formatDate(request.revisedDisclosureDate)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </div>
  );
};

export default COCRequestDetail;
